<template>
  <v-container fluid>
    <ActionsTable class="pb-4">
      <template v-slot:actions="{ dataset }">
        <avs-button
          :data="dataset"
          color="secondary"
          iconI="mdi-export"
          :spantext="
            $t('actions.export', {
              entity: $tc('categories.action.name', 2).toLowerCase()
            })
          "
          text
          @click="exportToExcel()"
        >
          <template v-slot:name>
            {{ $t("actions.export_") }}
          </template>
        </avs-button>
        <avs-createButton
          :to="{ name: 'ActionCreateUpdate' }"
          :spantext="
            $t('actions.create', {
              entity: $t('commons.action').toLowerCase()
            })
          "
          :name="
            $t('actions.new_female', {
              entity: $t('commons.action').toLowerCase()
            })
          "
        />
      </template>
    </ActionsTable>
  </v-container>
</template>
<script>
import { ActionsTable } from "@/components"
import { fmtNonConformityData } from "@/utils/download"
import NonConformityService from "@/services/NonConformityService"
import { toDatetime } from "@/utils/datetime"
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

export default {
  name: "ActionsView",
  components: { ActionsTable },
  computed: {
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  methods: {
    preFmtData(dataset) {
      return fmtNonConformityData(dataset)
    },
    async exportToExcel() {
      // 1. Récupérer toutes les actions du projet
      let resp
      try {
        resp = await NonConformityService.getListAllProjectActions(this.projectId)
      } catch (e) {
        return this.$store.commit("app/message", {
          msg: this.$t("messages.error.permission_denied"),
          color: "error"
        })
      }
      const allData = resp
      if (!allData.length) {
        return this.$store.commit("app/message", {
          msg: this.$t("messages.warning.no_data_to_export"),
          color: "warning"
        })
      }

      // 2. Préparer titre et nom de feuille (≤31 chars)
      const rawTitle = this.$tc("categories.nonconformity.action.name", 2).toUpperCase() // ex. "ACTIONS"
      const sheetName = rawTitle.slice(0, 31)

      // 3. En‑têtes traduites
      const headers = [
        this.$t("commons.nc_number"),
        this.$tc("categories.nonconformity.action.name", 1),
        this.$t("commons.date.end_date"),
        this.$t("commons.type"),
        `${this.$t("commons.cost")} [€]`,
        this.$t("commons.status.name"),
        this.$t("commons.intended_for"),
        this.$t("commons.monitoring_intended_for"),
        this.$t("commons.date.end_date"),
        this.$t("commons.status.name")
      ]

      // 4. Préparer les lignes de données
      const exportData = allData.map(item => [
        item.non_conformity_number,
        item.name,
        toDatetime(item.end_date),
        // Traduction du type d’action
        item.type_action === "Preventive"
          ? this.$t("commons.preventive")
          : item.type_action === "Corrective"
          ? this.$t("commons.corrective")
          : this.$t("commons.curative"),
        // Coût
        item.total_cost != null
          ? item.total_cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
          : "",
        // Statut
        item.status,
        // Assigné à
        item.user?.username || item.user || "N/A",
        // Suivi assigné à
        item.monitoring_user?.username || item.monitoring_user || "N/A",
        // Date fin suivi
        toDatetime(item.monitoring_end_date),
        // Statut suivi
        item.monitoring_status
      ])

      // 5. Créer workbook et worksheet
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet(sheetName)

      // 6. Titre en A1…J1
      ws.mergeCells(1, 1, 1, headers.length)
      ws.getCell("A1").value = rawTitle
      ws.getCell("A1").alignment = { horizontal: "center", vertical: "middle" }
      ws.getRow(1).font = { bold: true, size: 14 }

      // 7. Ligne 2 vide + en‑têtes ligne 3
      ws.addRow([])
      ws.addRow(headers)

      // 8. Données dès ligne 4
      exportData.forEach(row => ws.addRow(row))

      // 9. Ajuster largeur des colonnes
      ws.columns.forEach((col, i) => {
        const maxLen = Math.max(
          headers[i].length,
          ...exportData.map(r => (r[i] ? r[i].toString().length : 0))
        )
        col.width = maxLen + 2
      })

      // 10. Appliquer bordures fines & en‑têtes en gras
      ws.eachRow((row, idx) => {
        row.eachCell(cell => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        })
        if (idx === 3) row.font = { bold: true }
      })

      // 11. Générer et télécharger
      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      })
      saveAs(blob, `${sheetName.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`)

      // 12. Message de confirmation
      this.$store.commit("app/message", {
        msg: this.$t("Fichier Excel exporté avec succès"),
        color: "success"
      })
    }
  }
}
</script>
